import { Direction, Page, SwitchCondition } from "src/pageDefinitions/types";
import { CoreReduxState } from "src/utils/redux/store";
import invariant from "tiny-invariant";
import { typedRecord } from "src/utils/typeWrappers";
import {
  getSegmentFn,
  JsonScalar,
  resolveScalar,
  resolveValue,
} from "./values";

export type PageCondition = {
  key?: string;
  operator?: keyof typeof PageConditionOperators;
  value: JsonScalar | Array<JsonScalar>;

  or?: PageCondition;
};

type ConditionCheck = (params: {
  lookupValue: JsonScalar[];
  scalarValue: AnyJson;
  condition: PageCondition;
  state: CoreReduxState;
}) => boolean;

// Explicit return types required below to avoid this type from collapsing to any.
// Must be exported for schema generation
// eslint-disable-next-line import/no-unused-modules
export const PageConditionOperators = typedRecord<ConditionCheck>()({
  EXISTS({ scalarValue, condition }) {
    invariant(condition.key, "key must be defined");
    return scalarValue != null;
  },
  IN({ scalarValue, condition }) {
    invariant(
      Array.isArray(condition.value),
      "IN operator requires an array of values"
    );
    return condition.value.includes(scalarValue);
  },
  NOT_IN(params): boolean {
    return !PageConditionOperators.IN(params);
  },
  CONTAINS({ lookupValue, condition: { value } }) {
    return lookupValue?.includes(value as string);
  },
  NOT_CONTAINS(params): boolean {
    return !PageConditionOperators.CONTAINS(params);
  },
  CONTAINS_ANY({ lookupValue, condition: { value } }) {
    invariant(Array.isArray(value), "IN operator requires an array of values");
    return (
      lookupValue?.find((curr) => value.includes(curr as string | number)) !=
      null
    );
  },
  CONTAINS_NONE(params): boolean {
    return !PageConditionOperators.CONTAINS_ANY(params);
  },
  EQUALS({ scalarValue, condition }) {
    return scalarValue == null
      ? condition.value == null
      : scalarValue === condition.value;
  },
  NOT_EQUALS(params): boolean {
    return !PageConditionOperators.EQUALS(params);
  },
  GTE({ scalarValue, condition }) {
    return scalarValue >= condition.value;
  },
  LTE({ scalarValue, condition }) {
    return scalarValue <= condition.value;
  },
  ANY_SEGMENT({ condition: { value }, state }) {
    if (Array.isArray(value)) {
      return !!value.find((segmentName: string) => {
        return getSegmentFn(segmentName)(state);
      });
    }
    return getSegmentFn(value as string)(state);
  },
  ALL_SEGMENTS({ condition: { value }, state }) {
    if (Array.isArray(value)) {
      return !value.find((segmentName: string) => {
        return !getSegmentFn(segmentName)(state);
      });
    }
    return getSegmentFn(value as string)(state);
  },
  NO_SEGMENTS(params): boolean {
    return !PageConditionOperators.ANY_SEGMENT(params);
  },
});

// TODO: Allow for conditional resolution of props?

/**
 * Handles the processing of PageConditions.
 *
 * @returns an opaque numeric score that can be used to compare different sets of passing conditions.
 */
export function conditionsPass(
  conditions: PageCondition[],
  state: CoreReduxState,
  direction = Direction.FORWARD
): number {
  if (!conditions?.length) {
    return 1;
  }

  function evaluateCondition(condition: PageCondition): number {
    const { operator = "EQUALS", key } = condition;

    const lookupValue = resolveValue(key, state, direction);

    invariant(
      PageConditionOperators[operator],
      `Unknown operator: ${operator}`
    );
    const evaled = PageConditionOperators[operator]({
      condition,
      state,
      lookupValue,
      scalarValue: resolveScalar(lookupValue),
    });

    let ret = evaled ? 1 : 0;
    if (condition.or) {
      ret += evaluateCondition(condition.or);
    }

    return ret;
  }

  const evaluatedConditions: number[] = conditions.map(evaluateCondition);
  const conditionScore = evaluatedConditions.reduce(
    (accum, value) => accum + value
  );

  // If there are any false checks, then and them together.
  if (
    evaluatedConditions.filter(Boolean).length !== evaluatedConditions.length
  ) {
    return 0;
  }

  return conditionScore ? conditionScore + 1 : 0;
}

export function resolveSwitch(
  switchCondition: SwitchCondition,
  state: CoreReduxState,
  direction?: Direction
): Page {
  const { switch: key } = switchCondition;
  const lookupValue = resolveValue(key, state, direction);

  return (
    switchCondition.cases[lookupValue] || switchCondition.cases.default || {}
  );
}
